import { icons } from '../assets/icons';
import { themeService } from '../services/theme';
import { PROJECT_CONFIG, ReleaseInfo, FALLBACK_RELEASE, FALLBACK_PRERELEASE } from '../config/project-info';

export class NavbarComponent {
  private stableRelease: ReleaseInfo = FALLBACK_RELEASE;
  private prerelease: ReleaseInfo | null = FALLBACK_PRERELEASE;
  private onOpenReleaseModal: (release?: ReleaseInfo) => void;

  constructor(onOpenReleaseModal: (release?: ReleaseInfo) => void) {
    this.onOpenReleaseModal = onOpenReleaseModal;
  }

  public setReleases(stable: ReleaseInfo, prerelease: ReleaseInfo | null) {
    this.stableRelease = stable;
    this.prerelease = prerelease;

    // Desktop Stable badge
    const badge = document.getElementById('nav-version-badge');
    if (badge) badge.textContent = this.stableRelease.version;

    // Desktop Pre-release container & badge
    const preContainer = document.getElementById('nav-prerelease-container');
    const preBadge = document.getElementById('nav-prerelease-badge');
    if (preContainer) {
      if (this.prerelease) {
        preContainer.classList.remove('hidden');
        if (preBadge) preBadge.textContent = this.prerelease.version;
      } else {
        preContainer.classList.add('hidden');
      }
    }

    // Mobile badges
    const mobileBadge = document.getElementById('nav-version-badge-mobile');
    if (mobileBadge) mobileBadge.textContent = this.stableRelease.version;

    const mobilePreRow = document.getElementById('mobile-prerelease-row');
    const mobilePreBadge = document.getElementById('nav-prerelease-badge-mobile');
    if (mobilePreRow) {
      if (this.prerelease) {
        mobilePreRow.classList.remove('hidden');
        if (mobilePreBadge) mobilePreBadge.textContent = this.prerelease.version;
      } else {
        mobilePreRow.classList.add('hidden');
      }
    }
  }

  public render(): string {
    return `
      <header class="sticky top-0 z-40 w-full backdrop-blur-md bg-terminal-bgDark/85 border-b border-terminal-border transition-colors duration-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex items-center justify-between h-16">
            
            <!-- Logo & Brand -->
            <div class="flex items-center gap-3">
              <a href="#" class="flex items-center gap-2.5 text-matrix-500 font-extrabold text-xl tracking-wider group text-glow">
                <img 
                  src="/assets/mastui-logo.png" 
                  alt="Mastui logo" 
                  class="w-8 h-8 rounded-lg drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] group-hover:scale-105 transition-transform" 
                />
                <span class="tracking-wide">Mastui</span>
              </a>

              <!-- Version Badges (Stable & Pre-release) -->
              <div class="hidden sm:flex items-center gap-2">
                <!-- Stable Release Pill -->
                <button 
                  id="nav-release-btn"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-matrix-950/80 text-matrix-400 border border-matrix-600/40 hover:border-matrix-500 hover:text-matrix-300 transition-all shadow-glow-sm cursor-pointer"
                  title="View Stable Release Notes & Downloads"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-matrix-500 animate-pulse"></span>
                  <span id="nav-version-badge">${this.stableRelease.version}</span>
                  <span class="text-[10px] text-matrix-600">▼</span>
                </button>

                <!-- Pre-Release Pill -->
                <div id="nav-prerelease-container" class="${this.prerelease ? '' : 'hidden'}">
                  <button 
                    id="nav-prerelease-btn"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-amber-950/70 text-amber-300 border border-amber-500/40 hover:border-amber-400 hover:text-amber-200 transition-all cursor-pointer"
                    title="View Pre-Release Notes"
                  >
                    ${icons.flask('w-3 h-3 text-amber-400')}
                    <span id="nav-prerelease-badge">${this.prerelease ? this.prerelease.version : ''}</span>
                    <span class="text-[9px] uppercase font-bold px-1 rounded bg-amber-900/80 text-amber-200 border border-amber-700">Pre</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Desktop Navigation Links -->
            <nav class="hidden lg:flex items-center gap-1 text-sm font-medium text-terminal-muted">
              <a href="#features" class="px-3 py-1.5 rounded-lg hover:text-matrix-400 hover:bg-terminal-card border border-transparent hover:border-terminal-border transition-all">Features</a>
              <a href="#gallery" class="px-3 py-1.5 rounded-lg hover:text-matrix-400 hover:bg-terminal-card border border-transparent hover:border-terminal-border transition-all">Screenshots</a>
              <a href="#shortcuts" class="px-3 py-1.5 rounded-lg hover:text-matrix-400 hover:bg-terminal-card border border-transparent hover:border-terminal-border transition-all">Shortcuts</a>
              <a href="#install" class="px-3 py-1.5 rounded-lg hover:text-matrix-400 hover:bg-terminal-card border border-transparent hover:border-terminal-border transition-all">Install</a>
              
              <a 
                href="#/news" 
                class="px-3 py-1.5 rounded-lg text-amber-400 hover:text-amber-300 hover:bg-terminal-card border border-transparent hover:border-amber-700 flex items-center gap-1.5 transition-all font-semibold"
              >
                ${icons.zap('w-4 h-4')}
                <span>News</span>
              </a>

              <a 
                href="#/docs" 
                class="px-3 py-1.5 rounded-lg text-matrix-400 hover:text-matrix-300 bg-matrix-950/60 border border-matrix-800 hover:border-matrix-500 flex items-center gap-1.5 font-semibold transition-all shadow-glow-sm"
              >
                ${icons.bookOpen('w-4 h-4')}
                <span>Docs</span>
              </a>

              <a 
                href="#/community" 
                class="px-3 py-1.5 rounded-lg text-cyber-400 hover:text-cyber-300 hover:bg-terminal-card border border-transparent hover:border-cyber-700 flex items-center gap-1.5 transition-all"
              >
                ${icons.messageSquare('w-4 h-4')}
                <span>Community</span>
              </a>
            </nav>

            <!-- Actions (CRT Toggle, Mastodon, GitHub, Mobile Menu) -->
            <div class="flex items-center gap-2">
              
              <!-- CRT Scanline Toggle -->
              <button 
                id="crt-toggle-btn"
                class="p-2 rounded-lg text-matrix-600 hover:text-matrix-400 bg-terminal-card/80 border border-terminal-border hover:border-matrix-500/60 transition-all cursor-pointer"
                title="Toggle CRT Scanline Effect"
                aria-label="Toggle CRT Scanlines"
              >
                ${icons.tv('w-4 h-4')}
              </button>

              <!-- Mastodon Link -->
              <a 
                href="${PROJECT_CONFIG.mastodonUrl}"
                target="_blank"
                rel="me noopener noreferrer"
                class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-cyber-300 bg-cyber-950/60 hover:bg-cyber-900/80 border border-cyber-700/50 hover:border-cyber-400 transition-all"
                title="Follow Mastui on Mastodon"
              >
                ${icons.mastodon('w-4 h-4 text-cyber-400')}
                <span>@Mastui</span>
              </a>

              <!-- GitHub Repo Link -->
              <a 
                href="${PROJECT_CONFIG.githubUrl}"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-terminal-fg bg-terminal-card hover:bg-terminal-cardLight border border-terminal-border hover:border-matrix-500 transition-all shadow-glow-sm"
              >
                ${icons.github('w-4 h-4 text-matrix-500')}
                <span>GitHub</span>
              </a>

              <!-- Mobile Menu Toggle -->
              <button 
                id="mobile-menu-btn"
                class="lg:hidden p-2 rounded-lg text-matrix-500 hover:text-matrix-400 hover:bg-terminal-card border border-terminal-border transition-colors cursor-pointer"
                aria-label="Open menu"
              >
                <span id="menu-icon-open">${icons.menu('w-5 h-5')}</span>
                <span id="menu-icon-close" class="hidden">${icons.close('w-5 h-5')}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Drawer -->
        <div id="mobile-menu" class="hidden lg:hidden border-t border-terminal-border px-4 pt-3 pb-6 space-y-3 bg-terminal-bgDark/95 backdrop-blur-xl">
          <div class="flex items-center justify-between py-2 border-b border-terminal-border/60">
            <span class="text-xs font-mono text-terminal-muted">Latest Release</span>
            <button 
              id="mobile-nav-release-btn"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-matrix-950 text-matrix-400 border border-matrix-600/60"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-matrix-500 animate-pulse"></span>
              <span id="nav-version-badge-mobile">${this.stableRelease.version}</span>
            </button>
          </div>

          <div id="mobile-prerelease-row" class="${this.prerelease ? '' : 'hidden'} flex items-center justify-between py-2 border-b border-terminal-border/60">
            <span class="text-xs font-mono text-terminal-muted">Latest Pre-Release</span>
            <button 
              id="mobile-nav-prerelease-btn"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-amber-950 text-amber-300 border border-amber-600/60"
            >
              ${icons.flask('w-3 h-3 text-amber-400')}
              <span id="nav-prerelease-badge-mobile">${this.prerelease ? this.prerelease.version : ''}</span>
            </button>
          </div>

          <a href="#features" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-medium text-terminal-fg hover:bg-terminal-card hover:text-matrix-400">Features</a>
          <a href="#gallery" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-medium text-terminal-fg hover:bg-terminal-card hover:text-matrix-400">Screenshots & Showcase</a>
          <a href="#shortcuts" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-medium text-terminal-fg hover:bg-terminal-card hover:text-matrix-400">Keyboard Shortcuts</a>
          <a href="#install" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-medium text-terminal-fg hover:bg-terminal-card hover:text-matrix-400">Installation Hub</a>
          
          <a href="#/news" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-semibold text-amber-300 bg-amber-950/40 border border-amber-800/80 flex items-center gap-2">
            ${icons.zap('w-4 h-4 text-amber-400')}
            <span>News & Updates</span>
          </a>

          <a href="#/docs" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-semibold text-matrix-400 bg-matrix-950/60 border border-matrix-800 flex items-center gap-2">
            ${icons.bookOpen('w-4 h-4 text-matrix-500')}
            <span>Documentation (Live Wiki)</span>
          </a>
          
          <a href="#/community" class="mobile-nav-link block px-3 py-2 rounded-lg text-sm font-medium text-cyber-300 hover:bg-terminal-card flex items-center gap-2">
            ${icons.messageSquare('w-4 h-4 text-cyber-400')}
            <span>Community & Discussions</span>
          </a>

          <div class="pt-2 flex flex-col gap-2">
            <a 
              href="${PROJECT_CONFIG.mastodonUrl}"
              target="_blank"
              rel="me noopener noreferrer"
              class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold text-cyber-300 bg-cyber-950/80 border border-cyber-700"
            >
              ${icons.mastodon('w-4 h-4 text-cyber-400')}
              <span>Follow @Mastui on Mastodon</span>
            </a>
            
            <a 
              href="${PROJECT_CONFIG.githubUrl}"
              target="_blank"
              rel="noopener noreferrer"
              class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold text-terminal-fg bg-terminal-card border border-terminal-border"
            >
              ${icons.github('w-4 h-4 text-matrix-500')}
              <span>Star Repository on GitHub</span>
            </a>
          </div>
        </div>
      </header>
    `;
  }

  public bindEvents() {
    const crtBtn = document.getElementById('crt-toggle-btn');
    if (crtBtn) {
      crtBtn.addEventListener('click', () => {
        const isEnabled = themeService.toggleScanlines();
        crtBtn.classList.toggle('text-matrix-400', isEnabled);
        crtBtn.classList.toggle('text-matrix-800', !isEnabled);
      });
    }

    const releaseBtn = document.getElementById('nav-release-btn');
    if (releaseBtn) {
      releaseBtn.addEventListener('click', () => {
        this.onOpenReleaseModal(this.stableRelease);
      });
    }

    const prereleaseBtn = document.getElementById('nav-prerelease-btn');
    if (prereleaseBtn) {
      prereleaseBtn.addEventListener('click', () => {
        if (this.prerelease) {
          this.onOpenReleaseModal(this.prerelease);
        }
      });
    }

    const mobileReleaseBtn = document.getElementById('mobile-nav-release-btn');
    if (mobileReleaseBtn) {
      mobileReleaseBtn.addEventListener('click', () => {
        this.onOpenReleaseModal(this.stableRelease);
        this.closeMobileMenu();
      });
    }

    const mobilePreReleaseBtn = document.getElementById('mobile-nav-prerelease-btn');
    if (mobilePreReleaseBtn) {
      mobilePreReleaseBtn.addEventListener('click', () => {
        if (this.prerelease) {
          this.onOpenReleaseModal(this.prerelease);
        }
        this.closeMobileMenu();
      });
    }

    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    const mobileMenu = document.getElementById('mobile-menu');
    const iconOpen = document.getElementById('menu-icon-open');
    const iconClose = document.getElementById('menu-icon-close');

    if (mobileMenuBtn && mobileMenu) {
      mobileMenuBtn.addEventListener('click', () => {
        const isHidden = mobileMenu.classList.toggle('hidden');
        if (iconOpen && iconClose) {
          iconOpen.classList.toggle('hidden', !isHidden);
          iconClose.classList.toggle('hidden', isHidden);
        }
      });
    }

    // Close mobile menu on clicking any link
    document.querySelectorAll('.mobile-nav-link').forEach(link => {
      link.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    });
  }

  private closeMobileMenu() {
    const mobileMenu = document.getElementById('mobile-menu');
    const iconOpen = document.getElementById('menu-icon-open');
    const iconClose = document.getElementById('menu-icon-close');
    if (mobileMenu && !mobileMenu.classList.contains('hidden')) {
      mobileMenu.classList.add('hidden');
      if (iconOpen && iconClose) {
        iconOpen.classList.remove('hidden');
        iconClose.classList.add('hidden');
      }
    }
  }
}
